import { useCallback, useEffect, useRef } from "react";

/**
 * Custom hook that handles click outside events while excluding elements with
 * the given data-* attribute
 * @param handler - Function to call when clicking outside
 * @param dataDashIdentifier - data-* attribute to check
 * @param enabled - Whether the click outside detection is enabled
 */
export const useSelectiveClickOutside = <T extends HTMLElement = HTMLElement>(
  handler: () => void,
  dataDashIdentifier: string,
  enabled = true,
) => {
  const ref = useRef<T>(null);

  const handleClickOutside = useCallback(
    (event: MouseEvent) => {
      const target = event.target as Element;

      // Don't trigger if clicking inside the ref element, an element with the
      // given data-* attribute, or a Mantine dropdown portal (Select, Combobox, etc.)
      if (
        (ref.current && ref.current.contains(target)) ||
        target.closest(`[${dataDashIdentifier}="true"]`) ||
        target.closest("[data-combobox-option]") ||
        target.closest("[data-mantine-stop-propagation]")
      ) {
        return;
      }

      handler();
    },
    [dataDashIdentifier, handler],
  );
  useEffect(() => {
    if (!enabled) {
      return;
    }
    document.addEventListener("mousedown", handleClickOutside);

    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [handler, enabled, handleClickOutside]);

  return ref;
};
